import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useSaveCallerUserProfile } from '../hooks/useQueries';
import { Loader2, User } from 'lucide-react';

export default function ProfileSetup() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.displayName || '');
  const [role, setRole] = useState(user?.role || 'Operator');
  const saveProfile = useSaveCallerUserProfile();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await saveProfile.mutateAsync({ name: name.trim(), role });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-full max-w-sm px-4">
        <div className="bg-card border border-border rounded-lg shadow-card-dark overflow-hidden">
          <div className="px-6 py-4 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-electric-blue" />
              <span className="text-sm font-semibold tracking-wider uppercase">Profile Setup</span>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Welcome! Please set up your operator profile to continue.
            </p>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Display Name
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter your name"
                className="w-full bg-input border border-border rounded px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
                required
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Role
              </label>
              <select
                value={role}
                onChange={e => setRole(e.target.value)}
                className="w-full bg-input border border-border rounded px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
              >
                <option value="Operator">Operator</option>
                <option value="Quality Control Manager">Quality Control Manager</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
            {saveProfile.isError && (
              <p className="text-sm text-status-danger">Failed to save profile. Please try again.</p>
            )}
            <button
              type="submit"
              disabled={saveProfile.isPending || !name.trim()}
              className="w-full bg-electric-blue text-background font-bold py-2.5 rounded text-sm tracking-wider uppercase transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saveProfile.isPending ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
              ) : 'Continue to Dashboard'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
